import { UserActiveModulesSubscriptions } from '@/api/queries/getUserActiveModulesSubscriptions';
import { PermissionEnum } from '@/consts/roles';
import { User } from '@/redux/auth/authTypes';
import { HomeOutlined, LockOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { TFunction } from 'i18next';
import { FaUsers } from 'react-icons/fa';
import { IoIdCard, IoSettingsOutline } from 'react-icons/io5';
import { MdInventory2, MdShoppingBasket } from 'react-icons/md';
import { RiAdminLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';

interface MenuItem {
  key: string;
  icon?: React.ReactNode;
  label: string | React.ReactNode;
  url?: string;
  children?: MenuItem[];
  permissions?: PermissionEnum[];
  requireOwner?: boolean;
  requireSuperAdmin?: boolean;
  isLocked?: boolean;
  upgradeUrl?: string;
}

const userHasPermissions = (user: User | null) => {
  return (permissions: PermissionEnum[] = []) => {
    if (!user) return false;
    if (permissions.length === 0) return true;
    return permissions.every((permission) => user.role.permissions.includes(permission));
  };
};

const filterMenuItems = (
  items: MenuItem[],
  hasPermission: (perms: PermissionEnum[]) => boolean,
  isOwner: boolean,
  isSuperAdmin: boolean
): MenuItem[] => {
  return items
    .filter((item) => {
      if (item.requireOwner && !isOwner) return false;
      if (item.requireSuperAdmin && !isSuperAdmin) return false;
      if (item.permissions && !hasPermission(item.permissions)) return false;
      return true;
    })
    .map((item) => ({
      ...item,
      children: item.children
        ? filterMenuItems(item.children, hasPermission, isOwner, isSuperAdmin)
        : undefined,
    }))
    .filter((item) => !item.children || item.children.length > 0);
};

export const useGenerateItems = (
  t: TFunction,
  user: User | null,
  userActiveModulesSubscriptions?: UserActiveModulesSubscriptions[]
) => {
  const navigate = useNavigate();

  if (!user) return [];

  const isOwner = user?.role?.name === 'owner';
  const isSuperAdmin = user?.role?.name === 'super_admin';
  const checkPermissions = userHasPermissions(user);

  const hasInventoryActiveSubscription = userActiveModulesSubscriptions?.some(
    (subscription) =>
      subscription.moduleId.moduleId === 'INVENTORY' &&
      (subscription.status === 'active' || subscription.status === 'trial')
  );

  const menuItems: MenuItem[] = [
    {
      key: '1',
      icon: <HomeOutlined size={18} />,
      label: t('home'),
      url: '/',
    },
    {
      key: '2',
      icon: <MdShoppingBasket size={18} />,
      label: t('reports'),
      children: [
        { key: '2-1', label: t('salesPerItem'), url: '/reports/sales-per-item' },
        {
          key: '2-2',
          label: t('salesPerEmployee'),
          url: '/reports/sales-per-employee',
        },
        {
          key: '2-3',
          label: t('salesPerPayment'),
          url: '/reports/sales-per-payment',
        },
        {
          key: '2-4',
          label: t('salesPerCategory'),
          url: '/reports/sales-per-category',
        },
        {
          key: '2-5',
          label: t('salesPerStore'),
          url: '/reports/sales-per-store',
        },
        {
          key: '2-6',
          label: t('salesPerPOS'),
          url: '/reports/sales-per-pos',
        },
        {
          key: '2-7',
          label: t('invoices'),
          url: '/reports/invoices',
        },
      ],
    },
    {
      key: '3',
      icon: <MdShoppingBasket size={18} />,
      label: t('items'),
      permissions: [PermissionEnum.MANAGE_ITEMS],
      children: [
        { key: '3-1', label: t('all-items'), url: '/items/all' },
        { key: '3-2', label: t('categories'), url: '/items/categories' },
        { key: '3-3', label: t('modifiers'), url: '/items/modifiers' },
        { key: '3-4', label: t('discounts'), url: '/items/discounts' },
      ],
    },
    {
      key: '4',
      icon: (
        <MdInventory2
          size={18}
          className={!hasInventoryActiveSubscription ? 'text-gray-400' : ''}
        />
      ),
      label: (
        <div className="flex items-center justify-between w-full">
          <span className={!hasInventoryActiveSubscription ? 'text-gray-500' : ''}>
            {t('inventory')}
          </span>
          {!hasInventoryActiveSubscription && <LockOutlined className="text-gray-400 ml-2" />}
        </div>
      ),
      isLocked: !hasInventoryActiveSubscription,
      upgradeUrl: hasInventoryActiveSubscription ? undefined : '/settings/modules/inventory',
      children: hasInventoryActiveSubscription
        ? [
            { key: '4-1', label: t('suppliers'), url: '/inventory/suppliers' },
            { key: '4-2', label: t('purchase-orders'), url: '/inventory/purchase-orders' },
            { key: '4-3', label: t('transfer-orders'), url: '/inventory/transfer-orders' },
            { key: '4-4', label: t('inventory-count'), url: '/inventory/inventory-count' },
          ]
        : [
            {
              key: '4-locked',
              label: (
                <div className="flex flex-col gap-2 p-2">
                  <Button
                    type="primary"
                    size="small"
                    className="w-full hover:scale-105 transition-transform"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/settings/modules/inventory');
                    }}
                  >
                    {t('purchase')}
                  </Button>
                </div>
              ),
              url: undefined,
            },
          ],
    },
    {
      key: '5',
      icon: <IoIdCard size={18} />,
      label: t('employees'),
      children: [
        { key: '5-1', label: t('all-employees'), url: '/employees/all' },
        { key: '5-2', label: t('roles'), url: '/employees/roles', requireOwner: true },
      ],
    },
    {
      key: '6',
      icon: <FaUsers size={18} />,
      label: t('customers'),
      url: '/customers/all',
    },
    {
      key: '7',
      icon: <IoSettingsOutline size={18} />,
      label: t('settings'),
      children: [
        { key: '7-1', label: t('features'), url: '/settings/features', requireOwner: true },
        {
          key: '7-2',
          label: t('taxes'),
          url: '/settings/taxes',
          permissions: [PermissionEnum.MANAGE_TAXES],
        },
        {
          key: '7-3',
          label: t('receiptSettings'),
          url: '/settings/receipt',
        },
        {
          key: '7-4',
          label: t('stores'),
          url: '/settings/stores',
          requireOwner: true,
        },
        {
          key: '7-5',
          label: t('pos-devices'),
          url: '/settings/pos-devices',
        },
        {
          key: '7-6',
          label: t('tables'),
          children: [
            { key: '7-6-1', label: t('allTables'), url: '/tables/all' },
            { key: '7-6-2', label: t('tableCategories'), url: '/table-categories/all' },
          ],
        },
        {
          key: '7-7',
          label: t('subscription.title'),
          url: '/settings/upgrade',
        },
      ],
    },
    {
      key: 'admin-dashboard',
      icon: <RiAdminLine />,
      label: t('adminDashboard'),
      url: '/admin-dashboard',
      requireSuperAdmin: true,
    },
  ];

  return filterMenuItems(menuItems, checkPermissions, isOwner, isSuperAdmin);
};
